import type { Freshness, PeopleResponse } from '@pulse/contracts';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { DayControl, NotRecorded, useBusinessDate } from '@/components/pulse/day';
import { IncompleteMark } from '@/components/pulse/freshness';
import { PageHeader } from '@/components/pulse/page-header';
import { PersonCard } from '@/components/pulse/person-card';
import { WorkloadBar, WorkloadLegend } from '@/components/pulse/workload';
import { usePeople } from '@/domains/people/api';
import { useShell } from '@/domains/shell/api';

/** People's tabs (PPL-1), from the prototype's `peopleTabs()`. Team is for admins only (ACS-002 AC-2). */
export function PeopleTabs({ active }: { readonly active: 'everyone' | 'planning' | 'team' }): React.ReactElement {
  const shell = useShell();
  return <PeopleTabsView active={active} admin={shell.data?.roleLabel === 'Admin' && shell.data.member !== null} />;
}

export function PeopleTabsView({ active, admin }: { readonly active: 'everyone' | 'planning' | 'team'; readonly admin: boolean }): React.ReactElement {
  return (
    <div className="tabsbar">
      <nav className="tabs" aria-label="People view">
        <Link className="tab" to="/people" aria-current={active === 'everyone' ? 'page' : undefined}>
          Everyone
        </Link>
        <Link className="tab" to="/people/planning" aria-current={active === 'planning' ? 'page' : undefined}>
          Planning gaps
        </Link>
        {admin ? (
          <Link className="tab" to="/people/team" aria-current={active === 'team' ? 'page' : undefined}>
            Team
          </Link>
        ) : null}
      </nav>
    </div>
  );
}

/** People, Everyone tab (PPL-1 to PPL-3), built from the prototype's `viewPeople()`. */
export function PeoplePage(): React.ReactElement {
  const { date, today } = useBusinessDate();
  const page = usePeople(date, today);
  const shell = useShell();
  return (
    <>
      <PageHeader
        eyebrow={`People · ${page.data?.dateLabel ?? ''}`}
        title="Everyone"
        sub="Each person's day, built from their own GitHub and Teamwork activity."
        right={<DayControl date={date} today={today} />}
      />
      <PeopleTabs active="everyone" />
      {page.data !== undefined ? <PeopleView data={page.data} freshness={shell.data?.freshness ?? null} /> : null}
    </>
  );
}

export function PeopleView({ data, freshness }: { readonly data: PeopleResponse; readonly freshness: Freshness | null }): React.ReactElement {
  const scale = Math.max(0, ...data.workload.map((row) => row.summary.total));
  return (
    <>
      <section className="card pad stack" style={{ gap: 12 }} aria-labelledby="wlh">
        <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <h2 id="wlh">
            Workload <IncompleteMark figure="workload" freshness={freshness} />
          </h2>
          <WorkloadLegend />
        </div>
        <span style={{ fontSize: 13, color: 'var(--muted)' }}>
          Open items assigned to each person: GitHub issues, Teamwork tasks and story files. Bars share one scale.
        </span>
        {!data.recorded ? (
          <NotRecorded />
        ) : (
          <div>
            {data.workload.map((row) => (
              <div key={row.userId} className="wl-row">
                <Link to={row.href} className="row" style={{ gap: 10, textDecoration: 'none', color: 'var(--ink)', minWidth: 0 }}>
                  <span className="avatar sm" style={{ background: row.avatarColour }}>
                    {row.initials}
                  </span>
                  <span className="stack" style={{ minWidth: 0 }}>
                    <span style={{ fontWeight: 600 }}>{row.displayName}</span>
                    <span style={{ fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {row.next === null ? 'Nothing next' : `Next: ${row.next}`}
                    </span>
                  </span>
                </Link>
                <WorkloadBar summary={row.summary} scale={scale} />
                <span className="r num" style={{ fontWeight: 600 }}>
                  {row.summary.total}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
      <div className="people">
        {data.cards.map((card) => (
          <PersonCard key={card.userId} person={card} />
        ))}
      </div>
    </>
  );
}
